import React from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { GIFT_VOUCHER_PUBLIC_LINK } from "../config/features";
import Logo from "../assets/image/Logo";
import SocialIcons from "./SocialIcons";
import PrimaryButton from "./PrimaryButton";
import { NavLink } from "../types/navigation";
import "./Header.css";
import TranslateIcone from "assets/icone/TranslateIcone";
import LogoMobile from "assets/image/LogoMobile";
import MobileMenu from "assets/image/MobileMenu";
import XIcone from "assets/icone/XIcone";
import UpperLogoTablet from "assets/image/UpperLogoTablet";

const Header: React.FC = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
  const [isHeaderVisible, setIsHeaderVisible] = React.useState(true);
  const [isMobile, setIsMobile] = React.useState(false);
  const scrollSamplesRef = React.useRef<number[]>([]);
  const lastDirectionRef = React.useRef<'up' | 'down' | null>(null);

  const toggleLanguage = () => {
    const newLang = i18n.language === "he" ? "ru" : "he";
    i18n.changeLanguage(newLang);
    localStorage.setItem("i18nextLng", newLang);

    // הגדרת כיוון המסמך בהתאם לשפה
    document.dir = newLang === "he" ? "rtl" : "ltr";
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const scrollToContact = () => {
    const contactElement = document.getElementById("contact");
    if (contactElement) {
      contactElement.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  // אפקט לזיהוי גודל מסך
  React.useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth <= 809);
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);

    return () => {
      window.removeEventListener("resize", checkScreenSize);
    };
  }, []);

  // אפקט להגדרת כיוון המסמך בהתאם לשפה הנוכחית
  React.useEffect(() => {
    document.dir = i18n.language === "he" ? "rtl" : "ltr";
  }, [i18n.language]);

  // אפקט גלילה להדר - ממוצע משוקלל למניעת תנודות
  React.useEffect(() => {
    let ticking = false;
    const SAMPLE_SIZE = 5; // מספר דגימות לניתוח
    const WEIGHTS = [1, 2, 3, 4, 5]; // משקלים - הדגימות האחרונות חשובות יותר
    const SCROLL_THRESHOLD = 50; // סף לזיהוי שינוי כיוון משמעותי
    const MIN_SCROLL_FOR_HIDE = 100; // מינימום גלילה להתחלת הסתרה

    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const currentScrollY = Math.max(0, window.scrollY);
          
          // מניעת over-scroll
          const maxScroll = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
          const clampedScrollY = Math.min(currentScrollY, maxScroll);

          // תמיד הצג את ההדר בראש העמוד
          if (clampedScrollY <= 10) {
            if (!isHeaderVisible) {
              setIsHeaderVisible(true);
            }
            scrollSamplesRef.current = [];
            lastDirectionRef.current = null;
            ticking = false;
            return;
          }

          // הוספת דגימה חדשה
          scrollSamplesRef.current.push(clampedScrollY);
          if (scrollSamplesRef.current.length > SAMPLE_SIZE) {
            scrollSamplesRef.current.shift();
          }

          // חישוב ממוצע משוקלל רק כשיש מספיק דגימות
          if (scrollSamplesRef.current.length === SAMPLE_SIZE) {
            let weightedSum = 0;
            let weightTotal = 0;

            for (let i = 0; i < SAMPLE_SIZE; i++) {
              weightedSum += scrollSamplesRef.current[i] * WEIGHTS[i];
              weightTotal += WEIGHTS[i];
            }

            const weightedAverage = weightedSum / weightTotal;
            const difference = clampedScrollY - weightedAverage;

            // קביעת כיוון גלילה על בסיס ההפרש מהממוצע המשוקלל
            let currentDirection: 'up' | 'down' | null = null;

            if (Math.abs(difference) > SCROLL_THRESHOLD) {
              if (difference > 0) {
                currentDirection = 'down';
              } else if (difference < 0) {
                currentDirection = 'up';
              }

              // עדכון ההדר רק אם הכיוון השתנה באמת
              if (currentDirection && currentDirection !== lastDirectionRef.current) {
                lastDirectionRef.current = currentDirection;

                if (currentDirection === 'down' && clampedScrollY > MIN_SCROLL_FOR_HIDE) {
                  setIsHeaderVisible(false);
                } else if (currentDirection === 'up') {
                  setIsHeaderVisible(true);
                }
              }
            }
          }

          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [isHeaderVisible]);

  // אפקט לסגירת התפריט הנייד בלחיצה מחוץ לו
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isMobileMenuOpen) {
        const target = event.target as Element;
        const mobileNav = document.querySelector(".mobile-nav");
        const mobileMenuButton = document.querySelector(
          ".mobile-menu-button-header"
        );

        // בדיקה אם הלחיצה הייתה מחוץ לתפריט הנייד ולכפתור התפריט
        if (
          mobileNav &&
          !mobileNav.contains(target) &&
          mobileMenuButton &&
          !mobileMenuButton.contains(target)
        ) {
          setIsMobileMenuOpen(false);
        }
      }
    };

    if (isMobileMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isMobileMenuOpen]);

  const navLinks: NavLink[] = [
    { to: "/", label: t("home") },
    { to: "/workshops", label: t("workshops") },
    ...(GIFT_VOUCHER_PUBLIC_LINK
      ? [{ to: "/gift-voucher", label: t("giftVoucher"), highlighted: true }]
      : []),
    { to: "/gallery-detail", label: t("gallery") },
    { to: "/blog-detail", label: t("blog_detail") },
    { to: "/about-detail", label: t("about") },
    { to: "#", label: t("contact"), onClick: scrollToContact },
  ];

  return (
    <header
      className={`header-container ${
        isHeaderVisible ? "header-visible" : "header-hidden"
      }`}
      data-language={i18n.language === "ru" ? "ru" : "he"}
    >
      <div className="header-content">
        <div className="left-section">
          <button
            className="mobile-menu-button-header"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label={isMobileMenuOpen ? t("closeMenu") || "סגור תפריט" : t("openMenu") || "פתח תפריט"}
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-navigation"
          >
            {isMobileMenuOpen ? <XIcone /> : <MobileMenu />}
          </button>
        </div>
        <div className="desktop-logo-header">
          <Link to="/" onClick={scrollToTop} className="logo-link">
            <Logo />
          </Link>
        </div>
        <div className="center-section">
          <div className="mobile-logo-header">
            <Link to="/" onClick={scrollToTop}>
              <LogoMobile />
            </Link>
          </div>
          <div className="upper-logo-tablet">
            <Link to="/" onClick={scrollToTop}>
              <UpperLogoTablet />
            </Link>
          </div>
          <nav className="desktop-nav">
            {navLinks.map((link) =>
              link.onClick ? (
                <button
                  key={link.to}
                  onClick={link.onClick}
                  className="nav-link contact-link "
                >
                  {link.label}
                </button>
              ) : (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`nav-link${
                    location.pathname === link.to ? " active" : ""
                  }${link.highlighted ? " nav-link-highlighted" : ""}`}
                  onClick={scrollToTop}
                >
                  {link.label}
                </Link>
              )
            )}
          </nav>
        </div>

        <div className="right-section">
          <div className="left-section">
            <SocialIcons forceDesktop={true} />
          </div>

          <PrimaryButton
            className="language-toggle"
            onClick={toggleLanguage}
            icon={<TranslateIcone />}
            iconPosition={i18n.language === "he" ? "right" : "left"}
          >
            {isMobile
              ? i18n.language === "he"
                ? "RU"
                : "עב"
              : i18n.language === "he"
              ? "Russian"
              : "עברית"}
          </PrimaryButton>
        </div>
      </div>

      <div 
        id="mobile-navigation"
        className={`mobile-nav ${isMobileMenuOpen ? "open" : ""}`}
        role="navigation"
        aria-label={t("mainNavigation") || "ניווט ראשי"}
      >
        {navLinks.map((link) =>
          link.onClick ? (
            <div
              key={link.to}
              onClick={() => {
                link.onClick?.();
                setIsMobileMenuOpen(false);
              }}
              className="mobile-nav-link contact-link"
            >
              {link.label}
            </div>
          ) : (
            <Link
              key={link.to}
              to={link.to}
              className={`mobile-nav-link${
                location.pathname === link.to ? " active" : ""
              }${link.highlighted ? " nav-link-highlighted" : ""}`}
              onClick={() => {
                scrollToTop();
                setIsMobileMenuOpen(false);
              }}
            >
              {link.label}
            </Link>
          )
        )}

        <div className="mobile-social-icons">
          <SocialIcons forceDesktop={true} />
        </div>
      </div>
    </header>
  );
};

export default Header;
